import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Routes } from '../../enums/Routes';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { useGetTeamsQuery } from '../../hooks';
import { useSidebarRoutes } from '../../hooks/useSidebarRoutes';
import { useSafetyRepWorkQuery } from '../../hooks/queries/safetyRep/useSafetyRepWork';
import { useMyTasksQuery } from '../../hooks/queries/safetyRounds/useTaskQueries';
import { useMyDeviationsQuery } from '../../hooks/queries/safetyRounds/useDeviationQueries';
import { SideBarRouteItem } from '../../utils/SideBarRoutes';
import { questionLanguageFor } from '../../pages/FormSubmission/workEnvironmentFormView';
import { toDoCount } from '../../pages/SafetyRep/repTasksView';
import cn from 'classnames';
import { SidebarGroupItem, SidebarItem } from '../SidebarItem';
import { iconMap } from './iconMap';
import classes from './Sidebar.module.css';

type MainNavigationProps = {
  onNavigate?: () => void;
  collapsed?: boolean;
};

/**
 * Hovedmenyen. Én gruppe per rolle, med gruppenavn som overskrift.
 */
export const MainNavigation = ({ onNavigate, collapsed = false }: MainNavigationProps) => {
  const { t } = useTranslation();
  const { groups } = useSidebarRoutes();
  const idPrefix = useId();
  const getLabel = useRouteLabel();
  const countOf = useRouteCounts(groups.flatMap((group) => group.routes));

  return (
    <nav aria-label={t('sidebar.navigationLabel')} className={classes.navigation}>
      {groups.map((group) => {
        const headingId = `${idPrefix}-${group.key}`;

        return (
          <div key={group.key} className={classes.group}>
            <h2
              id={headingId}
              className={cn(classes.groupHeading, { 'ds-sr-only': collapsed })}
            >
              {group.label}
            </h2>
            <ul className={classes.list} aria-labelledby={headingId}>
              {group.routes.map((route) =>
                route.children ? (
                  <SidebarGroupItem
                    key={route.route}
                    icon={iconMap[route.icon]}
                    label={getLabel(route)}
                    pages={route.children}
                    onNavigate={onNavigate}
                    collapsed={collapsed}
                  />
                ) : (
                  <SidebarItem
                    key={route.route}
                    route={route.route}
                    icon={iconMap[route.icon]}
                    onNavigate={onNavigate}
                    collapsed={collapsed}
                    end={route.end}
                    count={countOf(route.route)}
                  >
                    {getLabel(route)}
                  </SidebarItem>
                ),
              )}
            </ul>
          </div>
        );
      })}
    </nav>
  );
};

// «Varsler» for verneombudet viser hvor mange som er uleste
const useRouteCounts = (routes: SideBarRouteItem[]) => {
  const { i18n } = useTranslation();
  const has = (route: Routes) => routes.some((item) => item.route === route);
  // The safety representative's «Mine oppgaver» counts the HMS work and the safety rounds, as the page does
  const { data: work } = useSafetyRepWorkQuery(questionLanguageFor(i18n.language), has(Routes.SafetyRepSigning));
  // The safety rounds' share of «Mine oppgaver» needs the module, as the route does
  const { data: tasks } = useMyTasksQuery(has(Routes.GeneralManagerTasks) || has(Routes.SafetyRepRoundPlans));
  const { data: deviations } = useMyDeviationsQuery(has(Routes.MyDeviations));
  const counts: Partial<Record<Routes, number>> = {
    [Routes.SafetyRepSigning]: toDoCount(tasks?.safetyRep?.count, work),
    [Routes.GeneralManagerTasks]: tasks?.generalManager?.count ?? 0,
    [Routes.MyDeviations]: deviations?.toDo.length ?? 0,
  };
  return (route: Routes) => counts[route] ?? 0;
};

// «Undersøkelser» viser hvilket team som er valgt
const useRouteLabel = () => {
  const { selectedTeamId } = useSelectedUnitContext();
  const { data: teams } = useGetTeamsQuery();
  const selectedTeam = teams?.find((team) => team.id === selectedTeamId);

  return (route: SideBarRouteItem) =>
    route.route === Routes.Surveys && selectedTeam
      ? `${route.label} (${selectedTeam.name})`
      : route.label;
};
